// Case studies — the long-form proof behind the resume bullets.
// Each one renders as a scripted tool-call trace followed by a result card.

export const caseStudies = {
  throughput: {
    slug: 'throughput',
    meta: 'AGITO GLOBAL · JAN 2026',
    title: 'P1 throughput crisis, logistics client',
    subtitle: '8 days · Claude API + custom MCP server',
    trace: [
      { tool: 'Read', args: 'resume.pdf', ms: 12 },
      { tool: 'Grep', args: 'experience --role "Applied AI Lead" --tag throughput', ms: 3, result: '1 match · agito-global/2026-01 · P1 incident' },
      { tool: 'mcp', args: 'sqlserver.explain_index_gaps  db=parcels  table=scan_events', ms: 418 },
      { tool: 'Replay', args: 'before → after', ms: 24 }
    ],
    metrics: {
      before: { label: 'before', value: '1,800', unit: 'parcels / hour', fill: 16 },
      after: { label: 'after', value: '11,000', unit: 'parcels / hour · 6× lift', fill: 100 }
    },
    points: [
      'Paired Claude API with a custom MCP server to diagnose SQL Server index gaps on the hot path.',
      'Used an Obsidian codebase graph to give Claude Code structural context for tracing race conditions.',
      'Ran measured spikes instead of arguing approaches; held scope through two mid-sprint architecture revisions.'
    ],
    tags: ['Claude API', 'MCP', 'SQL Server', 'Claude Code', 'Obsidian']
  },

  askmomentum: {
    slug: 'askmomentum',
    meta: 'AGITO GLOBAL · 2026',
    title: 'AskMomentum — operations assistant',
    subtitle: 'Python · LangChain · Ollama',
    trace: [
      { tool: 'Read', args: 'askmomentum/tool_layer.py', ms: 9 },
      { tool: 'mcp', args: 'sqlserver.list_databases', ms: 132, result: '28 databases · 12 GraphQL schemas · ElasticSearch' },
      { tool: 'Bench', args: 'hosted-api vs local-ollama --queries 20', ms: 2140 }
    ],
    metrics: {
      before: { label: 'hosted API', value: '$2.00', unit: 'per 20 queries', fill: 100 },
      after: { label: 'local Ollama', value: '$0.00', unit: 'per 20 queries', fill: 2 }
    },
    points: [
      'Answers live floor queries across 28 SQL Server databases, 12 GraphQL schemas and ElasticSearch logs.',
      'Replaced a hosted-API prototype with a locally served open-weight model, cutting inference cost to zero.',
      'Model-agnostic read-only tool layer — parameterized operations, no model-generated SQL — with async job APIs and per-answer tracing.',
      'Instrumentation attributed 99.7% of latency to inference, which drove the GPU sizing case.'
    ],
    tags: ['LangChain', 'Ollama', 'RAG', 'GraphQL', 'ElasticSearch']
  },

  'pr-review': {
    slug: 'pr-review',
    meta: 'AGITO GLOBAL · 2025',
    title: 'Multi-agent PR review pipeline',
    subtitle: 'Headless Claude Code + Azure DevOps',
    trace: [
      { tool: 'Read', args: '.azuredevops/branch-policies.yml', ms: 7 },
      { tool: 'Spawn', args: 'reviewers --dimensions correctness,rollback,off-by-one', ms: 18 },
      { tool: 'Verify', args: 'findings --adversarial', ms: 860, result: 'caught silent rollback failures pre-merge' }
    ],
    metrics: {
      before: { label: 'before', value: '100%', unit: 'baseline cycle time', fill: 100 },
      after: { label: 'after', value: '27.2%', unit: 'of baseline · 72.8% cut', fill: 27 }
    },
    points: [
      'Automated inline comments and approvals through Azure DevOps branch policies.',
      'Caught silent rollback failures and off-by-one defects before merge.',
      'Paired Claude Code with a knowledge base supplying scoped repository context per work item.'
    ],
    tags: ['Claude Code', 'Azure DevOps', 'CI/CD']
  },

  mcp: {
    slug: 'mcp',
    meta: 'AGITO GLOBAL · 2026',
    title: 'TypeScript MCP server for SQL Server ops',
    subtitle: 'Adopted org-wide by QA and Database teams',
    trace: [
      { tool: 'Read', args: 'sqlserver-agito-mcp/src/tools.ts', ms: 11 },
      { tool: 'mcp', args: 'listTools', ms: 24, result: 'ops · seed · snapshot · restore' },
      { tool: 'Measure', args: 'manual-db-maintenance --before --after', ms: 96 }
    ],
    metrics: {
      before: { label: 'before', value: '100%', unit: 'manual DB maintenance', fill: 100 },
      after: { label: 'after', value: '15%', unit: 'remaining · 85% cut', fill: 15 }
    },
    points: [
      'Automates SQL Server operations and test data generation across four environments.',
      'Adopted org-wide by the QA and Database teams.',
      'Published as a real repository — see `projects` for the live listing.'
    ],
    tags: ['TypeScript', 'MCP', 'SQL Server', 'Testing']
  }
};

export const caseStudyList = Object.keys(caseStudies);
